import React from "react";
import { Input, Select } from "antd";
import { useTranslations } from "translations/useTranslations";

import { useGetListOfTaxonomyByTaxonomyClass } from "api/useGetListOfTaxonomyByTaxonomyClass";

import { TaxonomyClass } from "modules/TaxonomyModule/consts/TaxonomyClassNames";
import { CLINIC_RELATION } from "modules/TaxonomyModule/consts/TaxonomyClassRelationsNames";

import { Preloader, PreloaderType } from "ui/Preloader";

import { SelectClinicsProps } from "./SelectClinics.types";

export const SelectClinics = React.memo<SelectClinicsProps>(({ provider, providerName, value = [], onChange }) => {
  const translations = useTranslations();

  const [search, setSearch] = React.useState("");

  const filterParams = React.useMemo(() => {
    const params = [];

    if (search) params.push(`search=${search}`);
    if (provider) params.push(`${CLINIC_RELATION.TO_PROVIDER}=${provider}`);

    return params.join("&");
  }, [provider, search]);

  const { data: clinics, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useGetListOfTaxonomyByTaxonomyClass(TaxonomyClass.CLINIC, filterParams);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = React.useCallback((e) => {
    const target = e.target as HTMLDivElement;
    if (!isLoading && hasNextPage && target.scrollTop + target.offsetHeight === target.scrollHeight) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isLoading]);

  const dropdownRender = React.useCallback((menu: React.ReactElement) => {
    if (isLoading) return <Preloader type={PreloaderType.SPINNER_BLOCK} />;

    return (
      <>
        {menu}
        {(isFetchingNextPage) && <Preloader type={PreloaderType.SPINNER_BLOCK} size="small" />}
      </>
    )
  }, [isFetchingNextPage, isLoading])

  const options = React.useMemo(() => clinics.map(clinic => ({ value: clinic.id, label: clinic.code })), [clinics]);

  const [clinicsEntered, setClinicsEntered] = React.useState(false);

  const handleInputClinics = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const COV_VALIDATION_REGEX = /^[a-f0-9-]{36}$/i;
    const DEFAULT_VALIDATION_REGEX = /^\d+$/;
    const validationRegex = providerName === "cov" ? COV_VALIDATION_REGEX : DEFAULT_VALIDATION_REGEX;

    const ids = e.target.value
      .split(",")
      .map(t => t.trim().toLowerCase())
      .filter(Boolean)
      .filter(t => validationRegex.test(t));

    if (!provider) {
      onChange?.([]);
      return;
    }

    const formatted = Array.from(new Set(ids.map(id => `item/clinic/${providerName}:${id}`)));
    setClinicsEntered(formatted.length > 0);
    onChange?.(formatted);
  }, [onChange, provider]);

  const isSelectDisabled = !provider || clinicsEntered;

  return (
    <>
      <Select
        mode="multiple"
        value={value}
        options={options}
        onChange={onChange}
        placeholder={translations["jobs.form.clinics.placeholder"]}
        loading={isLoading || isFetchingNextPage}
        onPopupScroll={handleScroll}
        dropdownRender={dropdownRender}
        onSearch={setSearch}
        disabled={isSelectDisabled} />
      <br /><br />
      <label>
        {translations["jobs.form.clinics.manualInputLabel"]}
        <Input.TextArea
          placeholder={translations["jobs.form.clinics.manualInputPlaceholder"]}
          autoSize={{ minRows: 2, maxRows: 8 }}
          onChange={handleInputClinics}
          disabled={!provider}
        />
      </label>
    </>
  );
});
